import { KeyboardEvent } from 'react';
import { Link } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/composer.pb';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, TextField, useFormField } from '@frontend/design-system';
import { SectionBase } from '../../../content-composer/section-base/section-base';
import { ToolbarView } from './types';

type Props = {
  link?: Link;
  setLink: (newLink: Link) => void;
  setView: (view: ToolbarView) => void;
  showLinkText?: boolean;
};

export const CustomToolbar = ({ link, setLink, setView, showLinkText = false }: Props) => {
  const { t } = useTranslation('content-composer');

  const linkFieldProps = useFormField({ type: 'text', value: link?.url }, [link?.url]);
  const linkTextFieldProps = useFormField({ type: 'text', value: '' });

  const hasCompleteLink =
    (showLinkText && linkFieldProps.value && linkTextFieldProps.value) || (!showLinkText && linkFieldProps.value);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSubmit();
    }
  };

  const onSubmit = () => {
    if (!hasCompleteLink) return;
    setLink({
      ...link,
      linkType: 'custom',
      url: linkFieldProps.value,
      ...(showLinkText ? { text: linkTextFieldProps.value } : {}),
    });
  };

  return (
    <SectionBase.Toolbar.Tool css={{ padding: 0 }}>
      <Button
        iconName='back-small'
        variant='secondary'
        size='small'
        onClick={() => (!!link?.url ? setView('selected') : setView('options'))}
        css={{
          borderBottomRightRadius: 0,
          borderRight: `solid 1px ${theme.colors.neutral20}`,
          borderTopRightRadius: 0,
          marginRight: theme.spacing(1),
          svg: { color: theme.colors.neutral70 },
          ':hover, :focus': { color: theme.colors.neutral10 },
        }}
      />
      {showLinkText && (
        <TextField
          {...linkTextFieldProps}
          name=''
          label=''
          autoFocus
          placeholder={t('Enter Link Text')}
          css={{
            width: 200,
            border: '0 !important',
            borderRight: `solid 1px ${theme.colors.neutral20} !important`,
            borderRadius: 0,
            boxShadow: 'none !important',
            height: theme.spacing(4),
          }}
          onKeyDown={handleKeyDown}
        />
      )}
      <TextField
        {...linkFieldProps}
        name=''
        label=''
        autoFocus={!showLinkText}
        placeholder={t('Enter Link URL')}
        css={{
          width: showLinkText ? 200 : 400,
          border: '0 !important',
          boxShadow: 'none !important',
          height: theme.spacing(4),
        }}
        onKeyDown={handleKeyDown}
      />
      <Button
        iconName='check'
        variant='primary'
        disabled={!hasCompleteLink}
        size='small'
        onClick={onSubmit}
        css={{
          borderBottomLeftRadius: 0,
          borderLeft: `solid 1px ${hasCompleteLink ? theme.colors.primary50 : theme.colors.status.disabled}`,
          borderTopLeftRadius: 0,
          marginLeft: theme.spacing(1),
          svg: { color: hasCompleteLink ? theme.colors.white : theme.colors.status.disabled },
          ':hover, :focus': { color: hasCompleteLink ? theme.colors.white : theme.colors.status.disabled },
        }}
      />
    </SectionBase.Toolbar.Tool>
  );
};
